import {
  Component,
  computed,
  DestroyRef,
  inject,
  model,
  ModelSignal,
  OnInit,
  signal,
  Signal,
  WritableSignal
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';
import { UiSpinnerDirective } from '@app/shared/ui/spinner/ui-spinner.directive';

import { TasksDataService } from '@app/shared/services/tasks-data.service';
import { SortDirection } from '@app/shared/types/enums/sort-direction.enum';
import { TaskSortField } from '@app/shared/types/enums/task-sort-field';
import { TaskFilters } from '@app/shared/types/models/task/task-filters.model';
import { Task } from '@app/shared/types/models/task/task.model';
import { TasksPageData } from '@app/shared/types/models/tasks-page-data';
import { UiDialogComponent } from '@app/shared/ui/dialog/ui-dialog.component';
import { TranslatePipe } from '@ngx-translate/core';
import { debounceTime, map } from 'rxjs/operators';
import { SelectParentTaskDialogData } from './select-parent-task-dialog-data';
import { SelectParentTaskTreeNodeComponent } from './select-parent-task-tree-node/select-parent-task-tree-node.component';

const PAGE_SIZE = 100;

@Component({
  selector: 'proffeo-select-parent-task-dialog',
  templateUrl: './select-parent-task-dialog.component.html',
  imports: [
    UiDialogComponent,
    TranslatePipe,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatRadioModule,
    MatDialogModule,
    SelectParentTaskTreeNodeComponent,
    UiButtonComponent,
    UiSpinnerDirective
  ]
})
export class SelectParentTaskDialogComponent implements OnInit {
  private readonly dialogRef = inject<MatDialogRef<SelectParentTaskDialogComponent, boolean | undefined>>(MatDialogRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly tasksDataService: TasksDataService = inject(TasksDataService);

  private readonly rootTasksPage: Signal<TasksPageData> = computed(() => {
    const text = this.searchLower();
    const filters: TaskFilters = text ? { text } : {};
    return this.tasksDataService.getProjectTasks(
      this.dialogData.projectId,
      0,
      PAGE_SIZE,
      TaskSortField.Name,
      SortDirection.ASC,
      undefined,
      filters
    )();
  });

  protected readonly dialogData: SelectParentTaskDialogData = inject(MAT_DIALOG_DATA);

  protected readonly search: WritableSignal<string> = signal('');

  protected readonly searchLower: Signal<string> = toSignal(
    toObservable(this.search).pipe(
      debounceTime(300),
      map(value => value.trim().toLowerCase())
    ),
    { initialValue: '' }
  );

  protected readonly selectedParentId: WritableSignal<string | null> = signal<string | null>(null);
  protected readonly expandedIds: ModelSignal<Set<string>> = model<Set<string>>(new Set());
  protected readonly forbiddenIds: WritableSignal<Set<string>> = signal<Set<string>>(new Set());
  protected readonly taskToMoveName = computed(() => this.dialogData.taskToMove?.name ?? '');

  protected readonly rootTasks: Signal<Task[]> = computed(() => {
    const page = this.rootTasksPage();
    if (!page?.content) return [];
    return page.content.map(taskSignal => taskSignal());
  });

  protected readonly initialParentId = computed(() => this.dialogData.taskToMove?.parentTask()?.id ?? null);
  protected readonly isChanged = computed(() => this.selectedParentId() !== this.initialParentId());

  protected readonly isMoving = signal(false);

  public ngOnInit(): void {
    this.markForbiddenToMoveTasks();
  }

  private markForbiddenToMoveTasks(): void {
    const taskToMove = this.dialogData.taskToMove;
    const initialForbiddenIds = new Set<string>(this.collectLoadedDescendantsIds(taskToMove.id));
    const ancestorsIds: string[] = [];
    let parentTask: Task | undefined = taskToMove.parentTask();

    if (parentTask?.id) {
      initialForbiddenIds.add(parentTask.id);
      this.selectedParentId.set(parentTask.id);
    }
    this.forbiddenIds.set(initialForbiddenIds);
    while (parentTask) {
      ancestorsIds.push(parentTask.id);
      parentTask = parentTask.parentTask();
    }
    if (ancestorsIds.length) {
      this.expandedIds.set(new Set(ancestorsIds));
    }
  }

  protected onSearchInput(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value);
  }

  protected selectRoot(): void {
    if (this.initialParentId() === null) return;
    this.selectedParentId.set(null);
  }

  protected confirmMove(): void {
    if (!this.isChanged() || this.isMoving()) return;

    const taskToMove = this.dialogData.taskToMove;
    const parentTaskId = this.selectedParentId();
    const currentParentId = taskToMove.parentTask()?.id ?? null;
    if (parentTaskId === currentParentId) return;

    this.isMoving.set(true);
    this.dialogRef.disableClose = true;

    this.tasksDataService
      .updateTaskParent(taskToMove, parentTaskId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.dialogRef.close(true),
        error: () => {
          this.isMoving.set(false);
          this.dialogRef.disableClose = false;
        }
      });
  }

  private collectLoadedDescendantsIds(rootTaskId: string | undefined): string[] {
    if (!rootTaskId) return [];
    const projectId = this.dialogData.projectId;
    const collected: string[] = [rootTaskId];
    const children = this.tasksDataService.getTasksByParent(projectId, rootTaskId)() ?? [];
    for (const child of children) {
      collected.push(...this.collectLoadedDescendantsIds(child.id));
    }
    return collected;
  }
}
